import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { useCreateCard } from '../modules/userCard/services/create';
import { useConfirmCard } from '../modules/userCard/services/confirm';
import { zodResolver } from '@hookform/resolvers/zod';
import { ICreateCard } from '../modules/userCard/types/Create';
import { ISaveCard } from '../modules/userCard/types/CardList';
import {
  otpSaveCardSchema,
  saveCardSchema,
} from '../modules/userCard/libs/saveCardSchema';
import { Drawer, DrawerClose, DrawerContent, DrawerTrigger } from './ui/drawer';
import { Form } from './ui/form';
import { t } from 'i18next';
import InputField from './form/InputFiled';
import CardNumberInputField from './form/CardInput';
import CardExpiryInputField from './form/CardExpireDate';
import { LoadingButton } from './ui/loadingButton';
import { Button } from './ui/button';
import { CustomOTP } from './form/CustomOTP';
import { Controller } from 'react-hook-form';
import { useForm } from 'react-hook-form';

type IProps = {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
};

export const SavaeCardDrawer = ({ drawerOpen, setDrawerOpen }: IProps) => {
  const [otpDrawer, setOtpDrawer] = useState(1);
  // const height = useCalcSafeHeight();
  const [cardId, setCardId] = useState(null);

  const { createCard, isPending } = useCreateCard();
  const { confirmCard, isPending: isPendingConfirm } = useConfirmCard();
  const form = useForm<ICreateCard>({
    resolver: zodResolver(saveCardSchema),
    mode: 'onChange',
    defaultValues: { title: '', pan: '', expiry: '' },
  });

  const otpForm = useForm<ISaveCard>({
    resolver: zodResolver(otpSaveCardSchema),
  });

  const onSubmit = async (data: ICreateCard) => {
    await createCard(data).then(res => {
      if (res.data.success) {
        setOtpDrawer(2);
        setCardId(res.data.card.id);
      }
    });
  };
  const onSubmitOtp = async (data: ISaveCard) => {
    if (cardId) {
      await confirmCard({ id: cardId, otp: data.otp }).then(res => {
        if (res.data.success) {
          setDrawerOpen(false);
          form.reset();
          otpForm.reset();
          setOtpDrawer(1);
          setCardId(null);
        } else {
          otpForm.reset();
        }
      });
    }
  };
  return (
    <Drawer
      open={drawerOpen}
      onOpenChange={(open) => {
        setDrawerOpen(open);
        if (!open) {
          form.reset();
          otpForm.reset();
          setOtpDrawer(1);
          setCardId(null);
        }
      }}
    >
      <DrawerTrigger asChild></DrawerTrigger>
      <DrawerContent
        // style={
        //   {
        //     // height: `${height}px`,
        //   }
        // }
        className={`transition-all duration-300 ease-in-out rounded-t-[24px]`}
      >
        {otpDrawer === 1 && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="flex flex-col h-full px-6 py-5 space-y-5">
                <p className="text-display-md text-center w-full">
                  {t('add-card')}
                </p>

                <div className="flex flex-col space-y-4 flex-1">
                  <InputField
                    name="title"
                    placeholder={t('title_card')}
                    inputClassName="focus:placeholder:text-transparent"
                    className="w-full h-14 text-[20px] font-semibold rounded-[16px] caret-button-primary px-4"
                  />

                  <div className="flex flex-col space-y-4">
                    <CardNumberInputField
                      name="pan"
                      placeholder="0000 0000 0000 0000"
                      inputClassName="focus:placeholder:text-transparent"
                      className="w-full h-14 text-[20px] font-semibold rounded-[16px] caret-button-primary px-4"
                    />
                    <CardExpiryInputField
                      name="expiry"
                      inputClassName="focus:placeholder:text-transparent"
                      className="w-[140px] h-14 text-[20px] font-semibold rounded-[16px] caret-button-primary px-4"
                    />
                    <p className="text-body-sm-regular text-text-secondary">
                      {t('only-humo')}
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-col justify-between space-y-4 pb-[calc(env(safe-area-inset-bottom)+8px)]">
                  <Button
                    type="submit"
                    disabled={isPending}
                    className="h-12 rounded-[12px] bg-button-primary text-white text-base font-semibold active:bg-button-primary focus:bg-button-primary disabled:opacity-70"
                  >
                    {isPending ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      t('next')
                    )}
                  </Button>
                  <DrawerClose asChild>
                    <Button
                      type="button"
                      className=" h-12 rounded-[12px]  text-text-primary bg-button-secondary font-semibold"
                      onClick={() => form.reset()}
                    >
                      {t('cancel')}
                    </Button>
                  </DrawerClose>
                </div>
              </div>
            </form>
          </Form>
        )}
        {otpDrawer === 2 && (
          <>
            {/* <div className="flex flex-row justify-between items-center">
          <div
            className="flex flex-row items-center space-x-[9px]"
            onClick={() => setOtpDrawer(1)}
          >
            <ArrowRightIcon className="rotate-180 h-4 w-2  " />
            <DrawerTitle className="text-display-md">
              {t('back')}
            </DrawerTitle>
          </div>
        </div> */}
            <div className="flex flex-col gap-y-4 pt-5 items-center px-2">
              <div className="flex flex-col space-y-2 items-center">
                <p className="text-display-lg">{t('cards.otp.title')}</p>
                <div>
                  <p className="text-body-sm-regular text-text-secondary  text-center">
                    {t('cards.otp.description')}
                  </p>
                </div>
              </div>
              <Form {...otpForm}>
                <form className="flex flex-col w-full ">
                  <Controller
                    control={otpForm.control}
                    name="otp"
                    render={({ field, fieldState: { error }, formState: { isSubmitted } }) => (
                      <CustomOTP
                        value={field.value || ''}
                        onChange={(val) => {
                          field.onChange(val);
                          if (error) otpForm.clearErrors('otp');
                        }}
                        length={6}
                        error={isSubmitted && !!error}
                      />
                    )}
                  />
                  <div className="flex flex-col space-y-4 mt-6 pb-[calc(env(safe-area-inset-bottom)+8px)]">
                    <LoadingButton
                      type="button"
                      loading={isPendingConfirm}
                      onClick={otpForm.handleSubmit(onSubmitOtp)}
                      className="w-full bg-button-primary h-12 text-[16px] font-semibold font-inter leading-5 tracking-[0px] text-text-white rounded-xl disabled:opacity-60"
                    >
                      {t('save')}
                    </LoadingButton>
                    <Button
                      type="button"
                      onClick={() => setOtpDrawer(1)}
                      className="w-full bg-button-secondary h-12 text-[16px] font-semibold font-inter leading-5 tracking-[0px] text-text-primary rounded-xl "
                    >
                      {t('back')}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};
